import React, { useState } from 'react';
import { useAuth } from './AuthProvider';
import './AuthForms.css';

interface ConfirmSignUpFormProps {
  email: string;
  onSuccess?: () => void;
  onBackToSignUp?: () => void;
}

export const ConfirmSignUpForm: React.FC<ConfirmSignUpFormProps> = ({ 
  email, 
  onSuccess, 
  onBackToSignUp 
}) => {
  const { confirmSignUp, resendConfirmationCode } = useAuth();
  const [confirmationCode, setConfirmationCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!confirmationCode) {
      newErrors.code = 'Please enter the confirmation code';
    } else if (confirmationCode.length !== 6) {
      newErrors.code = 'Confirmation code must be 6 digits';
    } else if (!/^\d+$/.test(confirmationCode)) {
      newErrors.code = 'Confirmation code must contain only numbers';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await confirmSignUp(email, confirmationCode);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Confirmation error:', error);
      
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.code === 'CodeMismatchException') {
        errorMessage = 'The confirmation code is incorrect. Please check and try again.';
      } else if (error.code === 'ExpiredCodeException') {
        errorMessage = 'The confirmation code has expired. Please request a new one.';
      } else if (error.code === 'LimitExceededException') {
        errorMessage = 'Too many attempts. Please wait a moment and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setErrors({});
    setResendMessage('');

    try {
      await resendConfirmationCode(email);
      setResendMessage('A new confirmation code has been sent to your email.');
    } catch (error: any) {
      console.error('Resend error:', error);
      
      let errorMessage = 'Could not resend code. Please try again.';
      
      if (error.code === 'LimitExceededException') {
        errorMessage = 'Too many requests. Please wait a few minutes before requesting another code.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsResending(false);
    }
  };

  const handleCodeChange = (value: string) => {
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setConfirmationCode(numericValue);
    
    // Clear error when user starts typing
    if (errors.code) {
      setErrors(prev => ({ ...prev, code: '' }));
    }
  };

  return (
    <div className="auth-form-container">
      <div className="auth-form">
        <h1 className="auth-title">Check Your Email</h1>
        <p className="auth-subtitle">
          We've sent a confirmation code to <strong>{email}</strong>. 
          Please enter the 6-digit code below to verify your account.
        </p>

        {errors.general && (
          <div className="error-message general-error" role="alert">
            {errors.general}
          </div>
        )}

        {resendMessage && (
          <div className="success-message" role="alert">
            {resendMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Confirmation Code Field */}
          <div className="form-group">
            <label htmlFor="confirmationCode" className="form-label">
              Confirmation Code
            </label>
            <input
              id="confirmationCode"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={`form-input code-input ${errors.code ? 'error' : ''}`}
              value={confirmationCode}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="Enter 6-digit code"
              required
              autoComplete="one-time-code"
              aria-describedby="code-help code-error"
              maxLength={6}
            />
            <div id="code-help" className="form-help">
              The code is 6 numbers long and was sent to your email address.
            </div>
            {errors.code && (
              <div id="code-error" className="error-message" role="alert">
                {errors.code}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading || confirmationCode.length !== 6}
          >
            {isLoading ? 'Verifying...' : 'Verify Account'}
          </button>
        </form>

        {/* Resend Code */}
        <div className="auth-links">
          <p>Didn't receive the code?</p>
          <button
            type="button"
            className="link-button"
            onClick={handleResendCode}
            disabled={isResending}
          >
            {isResending ? 'Sending...' : 'Send New Code'}
          </button>
        </div>

        {/* Back to Sign Up */}
        <div className="auth-switch">
          <p>Need to use a different email?</p>
          <button
            type="button"
            className="auth-button secondary"
            onClick={onBackToSignUp}
          >
            Back to Sign Up
          </button>
        </div>

        {/* Help Text */}
        <div className="auth-help">
          <p>
            <strong>Can't find the email?</strong> Check your spam or junk folder. 
            The email comes from "My Nerdy Son" and contains a 6-digit number code.
          </p>
        </div>
      </div>
    </div>
  );
};